import type {
  RenameComponentCmd,
  RenameSceneNodeCmd,
  SetFramePropertyCmd,
  SetInstancePropertyCmd,
  SetNodeParamOverrideCmd,
} from '../commandBuilders.js';
import type { AppState } from '../../state/appState.js';
import type { ActionExecution, EditorAction } from '../commandExecutor.js';
import { deepEqualValue, findSceneTargetByNodeIdFor } from './shared.js';

export function execSetFramePropertyFor(appState: AppState, cmd: SetFramePropertyCmd): ActionExecution<void> {
  const node = findSceneTargetByNodeIdFor(appState, cmd.nodeId)?.scene.nodes[cmd.nodeId];
  if (!node || node.kind !== 'frame') return { result: undefined, undoActions: [], changed: false };
  const previous = (node as unknown as Record<string, unknown>)[cmd.prop];
  if (Object.is(previous, cmd.value)) return { result: undefined, undoActions: [], changed: false };

  (node as unknown as Record<string, unknown>)[cmd.prop] = cmd.value;
  return {
    result: undefined,
    undoActions: [{ type: 'scene:setFrameProperty', nodeId: cmd.nodeId, prop: cmd.prop, value: previous } as EditorAction],
    changed: true,
  };
}

export function execSetInstancePropertyFor(appState: AppState, cmd: SetInstancePropertyCmd): ActionExecution<void> {
  const node = findSceneTargetByNodeIdFor(appState, cmd.nodeId)?.scene.nodes[cmd.nodeId];
  if (!node || (node.kind !== 'instance' && node.kind !== 'layer')) return { result: undefined, undoActions: [], changed: false };
  const previous = (node as unknown as Record<string, unknown>)[cmd.prop];
  if (Object.is(previous, cmd.value)) return { result: undefined, undoActions: [], changed: false };

  (node as unknown as Record<string, unknown>)[cmd.prop] = cmd.value;
  return {
    result: undefined,
    undoActions: [{ type: 'scene:setInstanceProperty', nodeId: cmd.nodeId, prop: cmd.prop, value: previous } as EditorAction],
    changed: true,
  };
}

export function execSetNodeParamOverrideFor(
  appState: AppState,
  cmd: SetNodeParamOverrideCmd,
): ActionExecution<void> {
  const node = findSceneTargetByNodeIdFor(appState, cmd.nodeId)?.scene.nodes[cmd.nodeId];
  if (!node || (node.kind !== 'instance' && node.kind !== 'layer')) {
    return { result: undefined, undoActions: [], changed: false };
  }
  const overrides = node.paramOverrides as Record<string, unknown>;
  const had = cmd.key in overrides;
  const previous = overrides[cmd.key];
  if (had === (cmd.value !== undefined) && deepEqualValue(previous, cmd.value)) {
    return { result: undefined, undoActions: [], changed: false };
  }

  // Replaced, not mutated: the dirty-rect diff compares the overrides object by
  // reference, so editing one in place is a change nothing downstream can see.
  const next = { ...overrides };
  if (cmd.value === undefined) delete next[cmd.key];
  else next[cmd.key] = cmd.value;
  node.paramOverrides = next;

  return {
    result: undefined,
    undoActions: [{
      type: 'scene:setNodeParamOverride',
      nodeId: cmd.nodeId,
      key: cmd.key,
      value: had ? (previous as never) : undefined,
    } as EditorAction],
    changed: true,
  };
}

export function execRenameSceneNodeFor(appState: AppState, cmd: RenameSceneNodeCmd): ActionExecution<void> {
  const node = findSceneTargetByNodeIdFor(appState, cmd.nodeId)?.scene.nodes[cmd.nodeId];
  if (!node || node.name === cmd.name) return { result: undefined, undoActions: [], changed: false };
  const previous = node.name;
  node.name = cmd.name;
  return {
    result: undefined,
    undoActions: [{ type: 'scene:rename', nodeId: cmd.nodeId, name: previous }],
    changed: true,
  };
}

export function execRenameComponentFor(appState: AppState, cmd: RenameComponentCmd): ActionExecution<void> {
  const component = appState.componentLibrary[cmd.componentId];
  if (!component || component.name === cmd.name) return { result: undefined, undoActions: [], changed: false };
  const previous = component.name;
  component.name = cmd.name;
  return {
    result: undefined,
    undoActions: [{ type: 'component:rename', componentId: cmd.componentId, name: previous }],
    changed: true,
  };
}
